import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Heart, Trash2, ShoppingBag, ShoppingCart, X, Calendar,Package } from 'lucide-react';
import Header from '../components/Header';
import './Favourites.css';

const Favourites = ({ savedItems, removeFavorite, addToCart, cartItems }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDesign, setSelectedDesign] = useState(null);
    const [toastMessage, setToastMessage] = useState('');

    const handleRemove = (design, e) => {
        e.stopPropagation();
        removeFavorite(design);
    };

    const handleCartAction = (design, e) => {
        if (e) e.stopPropagation();
        if (isInCart(design.id)) {
            navigate('/cart', { state: { from: '/favourites' } });
        } else {
            addToCart(design);
            showSuccessToast('Added to Cart!');
        }
    };

    const showSuccessToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => {
            setToastMessage('');
        }, 3000);
    };

    const handleCardClick = (design) => {
        setSelectedDesign(design);
    };

    const handleCloseModal = () => {
        setSelectedDesign(null);
    };

    const handleBookConsultation = () => {
        navigate('/home', { state: { openAppointment: 'fitting' } });
    };

    const isInCart = (designId) => {
        return cartItems?.some(item => item.id === designId);
    };


    // Filter out optimistic temp entries (missing product_name) before rendering
    // so the empty state shows correctly while the API call is in-flight
    const mappedItems = savedItems
        .filter(item => item.product_name)
        .map(item => ({
            id: item.product_id,
            favoriteId: item.id,
            title: item.product_name,
            price: item.product_price ? `Rs ${item.product_price}` : '',
            image: item.product_image,
            category: item.product_category,
            description: item.product_description,
            sizes: ["XS", "S", "M", "L", "XL", "XXL"]
        }));

    const getThumbClass = (title) => {
        if (title === 'Short Top') return 'thumb-short-top';
        if (title === 'Umbrella Skirt with Top') return 'thumb-umbrella-up';
        if (title === 'Cinderella Gown') return 'thumb-cinderella-up';
        if (title === 'Designer Gown') return 'thumb-designer-gown-up';
        if (title === 'Princess cut with Puff Sleeve') return 'thumb-zoom-out';
        if (title === 'Sleeveless with Embroidery') return 'thumb-align-sleeve';
        if (title === 'Blouse without Sleeve') return 'thumb-blouse-sleeveless';
        return '';
    };

    return (
        <div className="favourites-page">
            {toastMessage && (
                <div className="cart-toast">
                    <span className="toast-icon">✓</span>
                    {toastMessage}
                </div>
            )}
            <Header
                leftAction={
                    <button onClick={() => navigate(-1)} className="back-nav">
                        <ArrowLeft size={18} />
                        <span className="nav-text">Back</span>
                    </button>
                }
                rightAction={
                    <div className="header-actions">
                        <button className="tracking-btn" onClick={() => navigate('/tracking', { state: { from: '/favourites' } })}>
           <Package size={20} />
                        </button>
                        <button className="header-icon-btn" aria-label="Cart" onClick={() => navigate('/cart', { state: { from: '/favourites' } })}>
                            <ShoppingCart size={20} />
                            {cartItems?.length > 0 && (
                                <span className="cart-badge">{cartItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
                            )}
                        </button>
                    </div>
                }
            />
            <div className="cart-container">

                <div className="cart-card fade-in">
                    <div className="favourites-header">
                        <Heart size={24} className="cart-icon-pink" />
                        <h1>Your Favourites</h1>
                    </div>

                    {mappedItems.length === 0 ? (
                        <div className="empty-cart-content">
                            <div className="empty-icon-placeholder">
                                <Heart size={64} strokeWidth={1.5} />
                            </div>
                            <h2>Your favourites list is empty</h2>
                            <p className="empty-subtitle">
                                You haven't saved anything yet.<br />
                                Explore our collection to find something you love.
                            </p>
                            <button className="btn-browse" onClick={() => navigate('/gallery')}>
                                Browse Collection
                            </button>
                        </div>
                    ) : (
                        <div className="designs-grid">
                            {mappedItems.map((design, index) => {
                                // Skip temp entries (not yet confirmed from server, missing product details)
                                if (!design.title) return null;
                                const thumbClass = getThumbClass(design.title);
                                return (
                                    <div
                                        key={design.id}
                                        className={`design-card fade-in`}
                                        onClick={() => handleCardClick(design)}
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        <button
                                            className="btn-remove-fav"
                                            onClick={(e) => handleRemove(design, e)}
                                            title="Remove from Favourites"
                                        >
                                            <Trash2 size={18} />
                                        </button>

                                        <div className="design-image">
                                            <img
                                                src={design.image}
                                                alt={design.title}
                                                className={thumbClass}
                                            />
                                        </div>
                                        <div className="design-info">
                                            <h3 className="design-title">{design.title}</h3>

                                            <div className="design-footer">
                                                <p className="design-price">{design.price}</p>
                                                <button
                                                    className="btn-inquire-small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        addToCart(design);
                                                        showSuccessToast('Added to Cart!');
                                                    }}
                                                >
                                                    <ShoppingBag size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                                    Add to Cart
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {selectedDesign && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content scale-in" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={handleCloseModal}>
                            <X size={24} />
                        </button>
                        <div className="modal-layout">
                            <div className="modal-image">
                                <img
                                    src={selectedDesign.image}
                                    alt={selectedDesign.title}
                                    className={selectedDesign.image === '/images/designer-gown-1.jpg' ? 'modal-shift-up' : ''}
                                />
                            </div>
                            <div className="modal-details">
                                <h2>{selectedDesign.title}</h2>
                                <p className="modal-price">{selectedDesign.price}</p>
                                <p className="modal-description">{selectedDesign.description}</p>
                                <div className="modal-features">
                                    <h4>Features:</h4>
                                    <ul>
                                        <li>Premium quality fabrics</li>
                                        <li>Complimentary alterations</li>
                                    </ul>
                                </div>
                                <div className="modal-sizes fade-in">
                                    <h4 className="animated-sizes-label">Available in All Sizes!</h4>
                                </div>
                                <div className="modal-note">
                                    <span className="note-icon">💡</span>
                                    <p><strong>Note:</strong> All designs can be customized to your measurements and preferences. Book a fitting session to get started!</p>
                                </div>
                                <div className="modal-buttons">
                                    <button className="btn-book-consultation" onClick={handleBookConsultation}>
                                        <Calendar size={20} />
                                        Book Consultation
                                    </button>
                                    <button
                                        className={`btn-add-favorites active`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemove(selectedDesign, e);
                                            handleCloseModal();
                                        }}
                                    >
                                        <Heart size={20} fill="currentColor" />
                                        Saved
                                    </button>
                                    <button
                                        className={`btn-add-cart ${isInCart(selectedDesign.id) ? 'in-cart' : ''}`}
                                        onClick={() => handleCartAction(selectedDesign)}
                                    >
                                        <ShoppingBag size={20} />
                                        {isInCart(selectedDesign.id) ? 'Go to Cart' : 'Add to Cart'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Favourites;
